// File: src/components/OrientationGateDeniedBottomSheet.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { IoPeopleOutline } from 'react-icons/io5';

// Canonicalize and map to neutral audience labels (no "gay/orientation" terms)
function canonicalizeOrientation(raw) {
  const s = String(raw || '').trim().toLowerCase();
  const flat = s.replace(/[^a-z]/g, '');
  if (flat === 'gaymen' || flat === 'menlovingmen' || flat === 'mlm') return 'gay_men';
  if (flat === 'gaywomen' || flat === 'lesbian' || flat === 'wlw' || flat === 'womenlovingwomen') return 'gay_women';
  if (flat === 'straight' || flat === 'hetero' || flat === 'heterosexual' || flat === 'menandwomen') return 'straight';
  if (flat === 'bisexual' || flat === 'bi') return 'bisexual';
  if (flat === 'open' || flat === 'any' || flat === 'everyone' || flat === 'all') return 'open';
  return flat || 'open';
}

function audienceLabel(raw) {
  switch (canonicalizeOrientation(raw)) {
    case 'gay_men':   return 'men';
    case 'gay_women': return 'women';
    case 'straight':  return 'men or women';
    case 'bisexual':
    case 'open':
    default:          return 'everyone';
  }
}

/**
 * Map *classic* event config -> sheet orientation key
 * ('gay_men' | 'gay_women' | 'straight' | 'open')
 */
export function orientationForSheetFromClassic(evt = {}) {
  const p = String(evt?.purposeTag || '').toLowerCase();

  if (p === 'dating') {
    const o = String(evt?.classicOrientation || 'open').toLowerCase();
    if (o === 'gay_men') return 'gay_men';
    if (o === 'gay_women') return 'gay_women';
    if (o === 'straight' || o === 'heterosexual') return 'straight';
    return 'open'; // bisexual merged into open
  }

  if (p === 'friendship' || p === 'networking') {
    const g = String(evt?.classicGenderGate || 'all').toLowerCase();
    if (g === 'men_only')   return 'gay_men';   // show "men"
    if (g === 'women_only') return 'gay_women'; // show "women"
    return 'open';
  }

  return 'open';
}

export default function OrientationGateDeniedBottomSheet({
  orientation = 'open',
  onDismiss,
  onExploreEvents,
}) {
  const label = useMemo(() => audienceLabel(orientation), [orientation]);
  const overlayLockRef = useRef({ didLock: false, y: 0, prevTheme: '' });

  // ✅ Freeze background scroll + notch color while this sheet is mounted
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.content;

    const alreadyLocked =
      document.body.classList.contains('overlay-active') ||
      document.body.style.position === 'fixed';

    overlayLockRef.current.didLock = !alreadyLocked;

    // notch/status bar
    overlayLockRef.current.prevTheme = prevTheme || '#ffffff';
    if (meta) meta.content = '#999998';

    // Freeze scroll at current position (only if nothing else already locked it)
    if (!alreadyLocked) {
      const y = window.scrollY || window.pageYOffset || 0;
      overlayLockRef.current.y = y;

      document.body.style.top = `-${y}px`;
      document.body.classList.add('overlay-active');
    }

    return () => {
      // restore notch
      if (meta) meta.content = overlayLockRef.current.prevTheme || '#ffffff';

      // restore scroll only if we locked it
      if (overlayLockRef.current.didLock) {
        const y = overlayLockRef.current.y || 0;

        document.body.classList.remove('overlay-active');
        document.body.style.top = '';
        window.scrollTo(0, y);
      }
    };
  }, []);

  return (
    <div onClick={onDismiss} style={S.backdrop} role="presentation">
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Entry not allowed"
        style={S.sheet}
      >
        {/* Header (navbar) */}
        <div style={S.header}>
          <div style={S.headerTitle}>Event Audience</div>
        </div>

        {/* Body */}
        <div style={S.body}>
          {/* Illustration / Graphic placeholder */}
          <div style={S.illustration} aria-hidden>
            <IoPeopleOutline style={{ fontSize: 56, color: '#0b0b0b' }} />
          </div>

          {/* Headline + subheading */}
          <div style={S.section}>
            <div style={S.headline}>Entry limited to {label}</div>
            <div style={S.subheading}>
              This event is configured for <b>{label}</b>. Based on your profile, you can’t register for this event.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <div style={S.footerRow}>
            <button type="button" onClick={onExploreEvents} style={S.secondaryBtn}>
              See other events
            </button>
            <button type="button" onClick={onDismiss} style={S.primaryBtn}>
              OK
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(16px); opacity:0 } to { transform: translateY(0); opacity:1 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
        button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
      `}</style>
    </div>
  );
}

/* ===============================
         STYLE TOKENS
   (matches DateFilter sheet)
=============================== */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 520,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .25s ease-out',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 16px',
    borderBottom: '1px solid #eef0f2',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },

  body: {
    maxHeight: 'min(64vh, 560px)',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '8px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  section: { background: '#fff' },

  illustration: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 240,
    marginTop: 4,
    marginBottom: 6,
    border: '1px dashed #eef0f2',
    borderRadius: 12,
    background: '#fafafa',
  },

  headline: {
    fontSize: 16,
    fontWeight: 900,
    color: '#0b0b0b',
    margin: '2px 2px 6px',
    letterSpacing: '-0.01em',
  },
  subheading: {
    fontSize: 13.5,
    color: '#374151',
    lineHeight: 1.55,
    margin: '0 2px 6px',
  },

  footer: {
    padding: 12,
    borderTop: '1px solid #eef0f2',
  },
  footerRow: {
    display: 'flex',
    gap: 8,
  },
  secondaryBtn: {
    flex: 1,
    background: '#ffffff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
  primaryBtn: {
    flex: 1,
    background: '#000',
    color: '#fff',
    border: '1px solid #000',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};
